import { useEffect, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import {
  Alert,
  Anchor,
  Group,
  Loader,
  SegmentedControl,
  Select,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core'

import { navigate } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import Empty from 'src/components/Feedback/Empty/Empty'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { BULK_SMS } from 'src/graphql/activityLogs.graphql'
import {
  GET_TEXT_TEMPLATES,
  RESOLVE_TEXT_CONTENT,
  RESOLVE_TEXT_TEMPLATE,
} from 'src/graphql/textTemplates.graphql'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconPlane from 'src/icons/IconPlane'
import {
  SMS_SPAM_WARNING,
  SMS_TEMPLATE_HARD_LIMIT,
  SMS_TEMPLATE_HARD_LIMIT_MESSAGE,
  SMS_TEMPLATE_WARNING_THRESHOLD,
} from 'src/lib/sms'
import { SMS_TEMPLATE_PAGE_LIMIT } from 'src/lib/textTemplates'
import { SMS_TEMPLATE_VARIABLES } from 'src/lib/textTemplateVariables'
import CustomTab from 'src/pages/ActivitiesPage/components/CustomTab'
import { getEstimatedLength } from 'src/pages/SettingsPage/tabs/textTemplateTab/components/getEstimatedLength'

const MODE = { TEMPLATE: 'template', CUSTOM: 'custom' } as const
const VIEW = { RAW: 'raw', PREVIEW: 'preview' } as const

const modeOptions = [
  { value: MODE.TEMPLATE, label: 'Use template' },
  { value: MODE.CUSTOM, label: 'Write custom' },
]

const BulkSmsDrawer = ({ applicants, onClose, setLoading, setResults }) => {
  const [mode, setMode] = useState<string>(MODE.TEMPLATE)
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [customContent, setCustomContent] = useState('')
  const [cursorPosition, setCursorPosition] = useState(0)
  const [previewView, setPreviewView] = useState<string>(VIEW.RAW)

  const firstApplicant = applicants?.[0]

  //QUERIES:
  const { data: textTemplatesList, loading: templatesLoading } = useQuery(
    GET_TEXT_TEMPLATES,
    {
      variables: {
        pageInfo: {
          limit: SMS_TEMPLATE_PAGE_LIMIT,
          offset: 0,
        },
        orderBy: {
          field: 'title',
          direction: 'asc',
        },
      },
    }
  )

  const textTemplates = textTemplatesList?.textTemplates?.items || []

  const [
    resolveTextTemplate,
    { data: resolvedTemplateData, loading: resolvingTemplate },
  ] = useLazyQuery(RESOLVE_TEXT_TEMPLATE, { fetchPolicy: 'no-cache' })

  const [
    resolveTextContent,
    { data: resolvedContentData, loading: resolvingContent },
  ] = useLazyQuery(RESOLVE_TEXT_CONTENT, { fetchPolicy: 'no-cache' })

  // MUTATIONS:
  const [bulkSms, { loading: bulkSmsLoading }] = useMutation(BULK_SMS, {
    onCompleted: (data) => {
      const results = data?.bulkSms?.applicants
      if (setResults) {
        setResults(
          results?.length
            ? results.map((result) => ({
                applicantId: result.applicantId,
                success: result.success,
                errorMsg: result.errorMsg,
              }))
            : applicants.map((a) => ({
                applicantId: a.applicantId,
                success: true,
              }))
        )
      }
      toast('Text messages were sent successfully', 'success')
    },
    onError: () => {
      if (setResults) {
        setResults(
          applicants.map((a) => ({
            applicantId: a.applicantId,
            success: false,
            errorMsg: 'Failed to send SMS',
          }))
        )
      }
      toast('Unable to send bulk text message, please try again.', 'error')
    },
  })

  useEffect(() => {
    if (setLoading) {
      setLoading(bulkSmsLoading)
    }
  }, [bulkSmsLoading, setLoading])

  const selectedTemplate = textTemplates.find(
    (template) => template.textTemplateId === selectedTemplateId
  )

  // The raw (unresolved) body for whichever mode is active.
  const rawContent =
    mode === MODE.TEMPLATE ? selectedTemplate?.content ?? '' : customContent

  // Preview is only possible when there's a body to resolve and a recipient to
  // resolve it against.
  const canPreview = !!rawContent.trim() && !!firstApplicant

  // Insert a variable token at the current cursor position in the custom body,
  // then advance the cursor past it so successive chip clicks insert in order.
  const insertVariable = (variable: string) => {
    const token = `{{${variable}}}`
    setCustomContent(
      (prev) =>
        prev.slice(0, cursorPosition) + token + prev.slice(cursorPosition)
    )
    setCursorPosition((position) => position + token.length)
  }

  // Fall back to the raw view whenever a preview can't be produced.
  useEffect(() => {
    if (previewView === VIEW.PREVIEW && !canPreview) {
      setPreviewView(VIEW.RAW)
    }
  }, [previewView, canPreview])

  // Resolve template/custom variables against the first selected applicant when
  // the preview view is active.
  useEffect(() => {
    if (previewView !== VIEW.PREVIEW || !canPreview) {
      return
    }

    if (mode === MODE.TEMPLATE && selectedTemplateId) {
      resolveTextTemplate({
        variables: {
          textTemplateId: selectedTemplateId,
          applicantId: firstApplicant.applicantId,
        },
      })
      return
    }

    if (mode === MODE.CUSTOM) {
      const timeout = setTimeout(() => {
        resolveTextContent({
          variables: {
            content: customContent,
            applicantId: firstApplicant.applicantId,
          },
        })
      }, 400)
      return () => clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    previewView,
    canPreview,
    mode,
    selectedTemplateId,
    customContent,
    firstApplicant?.applicantId,
  ])

  const resolvedPreview =
    mode === MODE.TEMPLATE
      ? resolvedTemplateData?.resolveTextTemplate?.content
      : resolvedContentData?.resolveTextContent?.content
  const previewLoading =
    mode === MODE.TEMPLATE ? resolvingTemplate : resolvingContent

  // Custom messages support {{variables}} that expand per recipient on send,
  // so we gauge length against the estimated resolved length and use the
  // template thresholds (which leave headroom under Twilio's 1600 ceiling).
  const estimatedLength = getEstimatedLength(customContent)
  const showSpamWarning =
    mode === MODE.CUSTOM && estimatedLength > SMS_TEMPLATE_WARNING_THRESHOLD
  const overHardLimit =
    mode === MODE.CUSTOM && estimatedLength > SMS_TEMPLATE_HARD_LIMIT

  const handleSubmit = () => {
    bulkSms({
      variables: {
        applicants: applicants.map((applicant) => applicant.applicantId),
        ...(mode === MODE.TEMPLATE
          ? { textTemplateId: selectedTemplateId }
          : { content: customContent }),
      },
    })
  }

  const templateOptions = textTemplates.map((template) => ({
    value: template.textTemplateId,
    label: template.title,
  }))

  const canSubmit =
    applicants.length > 0 &&
    (mode === MODE.TEMPLATE
      ? !!selectedTemplateId
      : !!customContent.trim() && !overHardLimit)

  const disabledTooltip =
    mode === MODE.TEMPLATE
      ? 'Please select a template and at least one applicant to send the message'
      : 'Please enter a message and select at least one applicant to send'

  // Single message section: the Raw/Preview toggle switches the same box
  // between the editor (Raw) and the resolved-for-first-applicant preview.
  const messageSection = (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <Text size="xs">Message</Text>
        <SegmentedControl
          size="xs"
          value={previewView}
          onChange={setPreviewView}
          data={[
            { label: 'Raw', value: VIEW.RAW },
            { label: 'Preview', value: VIEW.PREVIEW, disabled: !canPreview },
          ]}
        />
      </div>

      {mode === MODE.CUSTOM && previewView === VIEW.RAW && (
        <div className="flex flex-col gap-2">
          <Text size="xs" c="dimmed">
            Click a variable to insert it — we&apos;ll fill it in for each
            recipient.
          </Text>
          <div className="flex flex-row flex-wrap gap-2">
            {SMS_TEMPLATE_VARIABLES.map((variable) => (
              <button
                key={variable.value}
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0"
                onClick={() => insertVariable(variable.value)}
              >
                <StyledBadge textTransform="capitalize" clickable>
                  {variable.name}
                </StyledBadge>
              </button>
            ))}
          </div>
        </div>
      )}

      {previewView === VIEW.PREVIEW ? (
        <div className="rounded-md border border-doubleNickel-gray-200 p-3 text-xs text-doubleNickel-gray-700">
          {previewLoading ? (
            <div className="flex items-center justify-center py-2">
              <Loader size="xs" color="gray" />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {firstApplicant?.name && (
                <Text size="xs" c="dimmed">
                  Preview for: {firstApplicant.name}
                </Text>
              )}
              <span className="whitespace-pre-wrap">{resolvedPreview}</span>
            </div>
          )}
        </div>
      ) : mode === MODE.CUSTOM ? (
        <>
          <Textarea
            placeholder="Type your message..."
            value={customContent}
            onChange={(event) => {
              setCustomContent(event.currentTarget.value)
              setCursorPosition(event.currentTarget.selectionStart)
            }}
            onClick={(event) =>
              setCursorPosition(event.currentTarget.selectionStart)
            }
            onKeyUp={(event) =>
              setCursorPosition(event.currentTarget.selectionStart)
            }
            autosize
            minRows={3}
            maxRows={8}
            size="xs"
            error={overHardLimit ? SMS_TEMPLATE_HARD_LIMIT_MESSAGE : undefined}
            classNames={{ input: 'text-xs' }}
          />
          <Text size="xs" c="dimmed" className="self-end">
            ~{estimatedLength}/{SMS_TEMPLATE_HARD_LIMIT} estimated
          </Text>
          {showSpamWarning && (
            <Alert
              icon={
                <IconAlertTriangle className="h-4 w-4 fill-none stroke-doubleNickel-warning-500" />
              }
              color="yellow"
              variant="light"
              classNames={{
                root: 'py-2',
                body: 'gap-0',
                message: 'text-xs',
              }}
            >
              {SMS_SPAM_WARNING}
            </Alert>
          )}
        </>
      ) : (
        <div className="rounded-md border border-doubleNickel-gray-200 p-3 text-xs text-doubleNickel-gray-700">
          <span className="whitespace-pre-wrap">{rawContent}</span>
        </div>
      )}
    </div>
  )

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-0.5 self-start rounded-lg bg-doubleNickel-gray-100 p-0.5">
          {modeOptions.map((option) => (
            <CustomTab
              key={option.value}
              selectedTab={mode}
              value={option.value}
              label={option.label}
              setSelectedTab={setMode}
              variant="segmented"
            />
          ))}
        </div>

        {mode === MODE.TEMPLATE ? (
          templatesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader size="sm" color="gray" />
            </div>
          ) : textTemplates.length > 0 ? (
            <>
              <div className="flex flex-row items-center justify-between">
                <Text size="xs">Template</Text>
                <div className="w-[250px]">
                  <Select
                    placeholder="Select a template"
                    data={templateOptions}
                    value={selectedTemplateId}
                    onChange={setSelectedTemplateId}
                    searchable
                    clearable
                    nothingFoundMessage="No templates found"
                    checkIconPosition="right"
                    size="xs"
                    classNames={{
                      input: 'text-xs text-right',
                      dropdown: 'border-doubleNickel-gray-200',
                      option:
                        'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                    }}
                  />
                </div>
              </div>
              {selectedTemplate && messageSection}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-6">
              <Empty
                title="No templates found"
                subtitle="Select a template or write a custom message"
              />
              <Anchor
                onClick={() => navigate('/settings?tab=sms')}
                className="mt-auto"
              >
                Add a template in settings
              </Anchor>
            </div>
          )
        ) : (
          messageSection
        )}
      </div>

      <Group className="mt-auto grid grid-cols-2">
        <Button
          text="Cancel"
          variant="outline"
          onClick={onClose}
          disabled={bulkSmsLoading}
        />
        <Tooltip disabled={canSubmit} label={disabledTooltip}>
          <Button
            text={'Send'}
            variant="filled"
            lefticon={<IconPlane />}
            disabled={!canSubmit}
            loading={bulkSmsLoading}
            onClick={() => {
              handleSubmit()
            }}
          />
        </Tooltip>
      </Group>
    </>
  )
}

export default BulkSmsDrawer
